import {
  convertDateFormatStrToDateTimeFormatOption,
  TextWithTooltip,
} from '@asset-metrix/portal-ui';

import { useStore } from '../../store';
import { FilterConfigDto } from '../../store/states/filterTypes';

import { getFilterFormatters } from './formatter';
import { MultiSelectFilter } from './MultiSelectFilter';
import { RangeDatePickerFilter } from './RangeDatePickerFilter';
import { RangeNumberPickerFilter } from './RangeNumberPickerFilter';
import { SearchFilter } from './SearchFilter';
import { SingleDatePickerFilter } from './SingleDatePickerFilter';
import { SingleSelectFilter } from './SingleSelectFilter';

import styles from './filters.module.scss';

type FilterProps = {
  isFetching: boolean;
  filterConfig: FilterConfigDto;
  entityId: string;
};

export function Filter({ isFetching, filterConfig, entityId }: FilterProps) {
  const {
    display: configDisplay,
    key,
    label: labelRaw,
    sublabel,
  } = filterConfig;
  const storeData = useStore((state) => state.filterData);

  const filterData = storeData[entityId]?.visualisedFilter?.[key];
  const clearable = !filterConfig.requiresSelection;

  const label = (
    <TextWithTooltip textProps={{ lineClamp: 1 }}>
      {labelRaw}
      {sublabel && (
        <span className={styles['pagefilter__filter__sublabel']}>
          {sublabel}
        </span>
      )}
    </TextWithTooltip>
  );

  if (filterData == null) {
    switch (configDisplay) {
      case 'single-select':
        return (
          <SingleSelectFilter
            entityId={entityId}
            filterKey={key}
            formatter={getFilterFormatters(filterConfig)}
            isFetching={isFetching}
            label={label}
            type={filterConfig.type}
            clearable={clearable}
          />
        );
      case 'single-date-picker':
        return (
          <SingleDatePickerFilter
            dateTimeFormatOptions={convertDateFormatStrToDateTimeFormatOption(
              filterConfig.formatOptions?.dateFormat
            )}
            entityId={entityId}
            filterKey={key}
            isFetching={isFetching}
            label={label}
            clearable={clearable}
          />
        );
      case 'multi-select':
        return (
          <MultiSelectFilter
            entityId={entityId}
            filterKey={key}
            formatter={getFilterFormatters(filterConfig)}
            isFetching={isFetching}
            label={label}
            type={filterConfig.type}
          />
        );
      case 'range-date-picker':
        return (
          <RangeDatePickerFilter
            dateTimeFormatOptions={convertDateFormatStrToDateTimeFormatOption(
              filterConfig.formatOptions?.dateFormat
            )}
            entityId={entityId}
            filterKey={key}
            label={label}
            isFetching={isFetching}
          />
        );
      case 'range-number-picker': {
        const digits = filterConfig.formatOptions?.digits;
        return (
          <RangeNumberPickerFilter
            allowDecimal={digits == null || digits > 0}
            entityId={entityId}
            filterKey={key}
            label={label}
            isFetching={isFetching}
            clearable={clearable}
          />
        );
      }
      case 'search':
        return (
          <SearchFilter
            entityId={entityId}
            label={label}
            filterKey={key}
            isFetching={isFetching}
          />
        );
    }
  }

  switch (filterData.display) {
    case 'single-select':
      return (
        <SingleSelectFilter
          filterData={filterData}
          entityId={entityId}
          filterKey={key}
          formatter={getFilterFormatters(filterConfig)}
          isFetching={isFetching}
          label={label}
          clearable={clearable}
          type={filterData.type}
        />
      );
    case 'single-date-picker':
      return (
        <SingleDatePickerFilter
          dateTimeFormatOptions={convertDateFormatStrToDateTimeFormatOption(
            filterConfig.formatOptions?.dateFormat
          )}
          filterData={filterData}
          entityId={entityId}
          filterKey={key}
          isFetching={isFetching}
          label={label}
          clearable={clearable}
        />
      );
    case 'multi-select':
      return (
        <MultiSelectFilter
          filterData={filterData}
          entityId={entityId}
          filterKey={key}
          formatter={getFilterFormatters(filterConfig)}
          isFetching={isFetching}
          label={label}
          type={filterData.type}
        />
      );
    case 'range-date-picker':
      return (
        <RangeDatePickerFilter
          dateTimeFormatOptions={convertDateFormatStrToDateTimeFormatOption(
            filterConfig.formatOptions?.dateFormat
          )}
          entityId={entityId}
          label={label}
          filterData={filterData}
          filterKey={key}
          isFetching={isFetching}
        />
      );
    case 'range-number-picker': {
      const digits = filterConfig.formatOptions?.digits;
      const factor = filterConfig.formatOptions?.factor ?? undefined;
      const suffix = filterConfig.formatOptions?.postfix ?? undefined;

      return (
        <RangeNumberPickerFilter
          allowDecimal={digits == null || digits > 0}
          clearable={clearable}
          entityId={entityId}
          factor={factor}
          filterData={filterData}
          filterKey={key}
          isFetching={isFetching}
          label={label}
          suffix={suffix}
        />
      );
    }
    case 'search':
      return (
        <SearchFilter
          entityId={entityId}
          label={label}
          filterData={filterData}
          filterKey={key}
          isFetching={isFetching}
        />
      );
  }
}
